// Phase 4d — Pre-Award Workspace V2 cockpit.
//
// A one-pre-award cockpit mirroring Contract Workspace V2 (corrective §5) at
// /delivery/pre-award/:id/v2: a left lens switcher + screen nav, the active
// screen, and a right rail showing the selected readiness area's detail.
// Three screens:
//   - Readiness Board: the 8 SDS §3.3 areas as status cards with inline status
//     edit + an "X/8 Ready" rollup gate.
//   - Bid Baseline: the frozen proposal snapshot (read-only) zou proxies from
//     pangaea via GetBidBaseline.
//   - Launch: PreAwardProject state + the Begin Award Comparison CTA.
//
// Reuses the Phase 0b pre-award hooks (usePreAwardWorkspace /
// useUpsertPreAwardReadiness / useBeginAwardComparison) and adds useBidBaseline.
// The V1 page at /delivery/pre-award/:id stays as the tabs-based fallback.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageBody } from '../../../../components/layout';
import { ErrorState } from '../../_shared/components/PageStates';
import { useBidBaseline, usePreAwardWorkspace, useUpsertPreAwardReadiness } from '../hooks';
import { AwardIntakePanel } from './AwardIntakePanel';
import { STATUS_TONE } from './constants';
import { ReadinessDetailEditor } from './ReadinessDetailEditor';
// The 8 SDS §3.3 readiness areas, in canonical order — kept local so the board
// renders all 8 cards even before the seed rows arrive over the wire.
const READINESS_AREAS = [
    'Staffing',
    'Financial',
    'Subcontractor',
    'Schedule',
    'Governance',
    'Customer',
    'Artifacts',
    'Risk'
];
const READINESS_STATUSES = [
    'NotStarted',
    'InProgress',
    'Ready',
    'Blocked'
];
// Lenses are productivity filters over the screens, not permission walls — each
// shows a subset (same shape as Contract Workspace V2's LENSES).
const LENSES = [
    {
        id: 'command',
        label: 'Command',
        screens: [
            'readiness',
            'baseline',
            'launch'
        ]
    },
    {
        id: 'operations',
        label: 'Operations',
        screens: [
            'readiness',
            'launch'
        ]
    },
    {
        id: 'finance',
        label: 'Finance',
        screens: [
            'readiness',
            'baseline'
        ]
    }
];
const SCREEN_LABELS = {
    readiness: 'Readiness Board',
    baseline: 'Bid Baseline',
    launch: 'Launch'
};
export function PreAwardWorkspaceV2() {
    const { id } = useParams();
    const preAwardProjectId = id ?? '';
    const navigate = useNavigate();
    const { data, isLoading, isError, error } = usePreAwardWorkspace(preAwardProjectId);
    // One workspace correlation per mount — re-used across all readiness saves so
    // the audit timeline groups the session under one row (Phase 0b F-D).
    const workspaceCorrelationId = useMemo(()=>crypto.randomUUID(), []);
    // Two separate mutation instances so board-select and detail-editor state
    // never bleed: isError/isPending are per-instance, not per-call. Both carry
    // the same correlation header so the audit timeline still groups them.
    const boardUpsert = useUpsertPreAwardReadiness(workspaceCorrelationId);
    const detailUpsert = useUpsertPreAwardReadiness(workspaceCorrelationId);
    const [lensId, setLensId] = useState('command');
    // The active screen lives in ?screen= (validated values only), with the
    // URL as the single source of truth. Deriving — not initializing — is
    // load-bearing: the tray/toast "View" actions navigate to ?screen=launch,
    // and when the cockpit is ALREADY mounted (user parked on Readiness
    // mid-processing) a same-route navigation never remounts the component,
    // so an initializer-only read would silently ignore the deep-link.
    const [searchParams, setSearchParams] = useSearchParams();
    const screenParam = searchParams.get('screen');
    const screen = screenParam === 'launch' || screenParam === 'baseline' || screenParam === 'readiness' ? screenParam : 'readiness';
    const setScreen = (s)=>setSearchParams((prev)=>{
            const next = new URLSearchParams(prev);
            if (s === 'readiness') next.delete('screen');
            else next.set('screen', s);
            return next;
        }, {
            replace: true
        });
    const [selectedArea, setSelectedArea] = useState(null);
    const lens = useMemo(()=>LENSES.find((l)=>l.id === lensId) ?? LENSES[0], [
        lensId
    ]);
    const visibleScreens = lens.screens;
    const activeScreen = visibleScreens.includes(screen) ? screen : visibleScreens[0];
    // URL vs lens precedence: the latest explicit intent wins. A deep-link
    // (tray/toast View navigating ?screen=launch in place) switches the lens
    // to one that can show the requested screen — otherwise the param would
    // be silently swallowed by the activeScreen clamp under e.g. the finance
    // lens. The inverse (user picks a lens that hides the current screen) is
    // handled in handleLensChange, which clamps the screen AND the URL, so
    // this effect only fires for genuine external navigations.
    useEffect(()=>{
        if (visibleScreens.includes(screen)) return;
        const containing = LENSES.find((l)=>l.screens.includes(screen));
        if (containing) setLensId(containing.id);
    }, [
        screen,
        visibleScreens
    ]);
    const handleLensChange = (nextLensId)=>{
        setLensId(nextLensId);
        const nextLens = LENSES.find((l)=>l.id === nextLensId) ?? LENSES[0];
        // Clamp the URL along with the view so a hidden screen never lingers
        // as a stale param that would mislead on copy/share/refresh.
        if (!nextLens.screens.includes(screen)) setScreen(nextLens.screens[0]);
    };
    // Only fetch the frozen baseline when the Baseline screen is actually open —
    // it's a second round-trip the Readiness/Launch screens never need.
    const baselineQuery = useBidBaseline(preAwardProjectId, activeScreen === 'baseline');
    const readinessByArea = useMemo(()=>{
        const map = new Map();
        for (const r of data?.readiness ?? []){
            map.set(r.area, {
                status: r.status,
                dataJson: r.dataJson
            });
        }
        return map;
    }, [
        data?.readiness
    ]);
    const readyCount = useMemo(()=>READINESS_AREAS.filter((a)=>readinessByArea.get(a)?.status === 'Ready').length, [
        readinessByArea
    ]);
    if (!id) return /*#__PURE__*/ _jsx(Navigate, {
        to: "/delivery",
        replace: true
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-3 p-8",
                "data-testid": Testids.deliveryShared.loadingSkeleton,
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-8 w-1/3"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "grid grid-cols-4 gap-3",
                        children: [
                            0,
                            1,
                            2,
                            3
                        ].map((i)=>/*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-28"
                            }, i))
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-48 w-full"
                    })
                ]
            })
        });
    }
    if (isError || !data) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("div", {
                className: "p-8",
                children: /*#__PURE__*/ _jsx(ErrorState, {
                    title: "Pre-award workspace unavailable",
                    error: error instanceof Error ? error : 'No workspace found for this id.'
                })
            })
        });
    }
    const selectedDetail = selectedArea ? {
        area: selectedArea,
        ...readinessByArea.get(selectedArea) ?? {
            status: 'NotStarted',
            dataJson: ''
        }
    } : null;
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex h-full flex-col gap-4 p-6",
            "data-testid": Testids.preAwardV2.workspaceRoot,
            "data-pre-award-id": data.preAwardProjectId,
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: "flex items-start justify-between gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h1", {
                                    className: "text-2xl font-semibold",
                                    children: "Pre-Award Workspace"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: [
                                        "Source proposal ",
                                        /*#__PURE__*/ _jsx("code", {
                                            className: "text-xs",
                                            children: data.proposalProjectId
                                        }),
                                        " · State",
                                        ' ',
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300",
                                            children: data.state
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("label", {
                            className: "flex items-center gap-2 text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-muted-foreground",
                                    children: "Lens"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: lensId,
                                    onValueChange: handleLensChange,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            className: "h-8 w-auto",
                                            "data-testid": Testids.preAwardV2.lensSelect,
                                            "aria-label": "Lens",
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: LENSES.map((l)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: l.id,
                                                    children: l.label
                                                }, l.id))
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("nav", {
                    className: "flex gap-2 border-b pb-2",
                    children: visibleScreens.map((s)=>/*#__PURE__*/ _jsx(SpecraButton, {
                            variant: s === activeScreen ? 'default' : 'ghost',
                            size: "sm",
                            onClick: ()=>setScreen(s),
                            "data-testid": Testids.preAwardV2.screenTab,
                            "data-screen": s,
                            children: SCREEN_LABELS[s]
                        }, s))
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid flex-1 grid-cols-3 gap-4 overflow-hidden",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "col-span-2 overflow-auto",
                            children: [
                                activeScreen === 'readiness' && /*#__PURE__*/ _jsx(ReadinessBoard, {
                                    preAwardProjectId: data.preAwardProjectId,
                                    readinessByArea: readinessByArea,
                                    readyCount: readyCount,
                                    selectedArea: selectedArea,
                                    onSelect: setSelectedArea,
                                    upsert: boardUpsert
                                }),
                                activeScreen === 'baseline' && /*#__PURE__*/ _jsx(BidBaselineScreen, {
                                    query: baselineQuery,
                                    bidBaselineId: data.bidBaselineId
                                }),
                                activeScreen === 'launch' && /*#__PURE__*/ _jsx(LaunchScreen, {
                                    preAwardProjectId: data.preAwardProjectId,
                                    zouProjectId: data.zouProjectId,
                                    state: data.state,
                                    readyCount: readyCount,
                                    onComparison: (csId)=>navigate(`/delivery/pre-award/${data.preAwardProjectId}/comparison/${csId}`)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(DetailRail, {
                            detail: selectedDetail,
                            preAwardProjectId: data.preAwardProjectId,
                            readinessByArea: readinessByArea,
                            upsert: detailUpsert
                        })
                    ]
                })
            ]
        })
    });
}
// --- Readiness Board: 8 area cards + inline status edit + X/8 rollup gate ---
function ReadinessBoard({ preAwardProjectId, readinessByArea, readyCount, selectedArea, onSelect, upsert, readOnly }) {
    const allReady = readyCount === READINESS_AREAS.length;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.preAwardV2.readinessRollup,
                "data-ready-count": readyCount,
                className: `flex items-center justify-between rounded-lg border px-4 py-3 text-sm ${allReady ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-muted/40'}`,
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "font-medium",
                        children: [
                            readyCount,
                            "/",
                            READINESS_AREAS.length,
                            " Ready"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground",
                        children: allReady ? 'All readiness areas are Ready — clear to launch the award comparison.' : 'Every area must reach Ready before the award comparison is launch-clean.'
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "grid grid-cols-2 gap-3",
                children: READINESS_AREAS.map((area)=>{
                    const status = readinessByArea.get(area)?.status ?? 'NotStarted';
                    const isSelected = selectedArea === area;
                    return /*#__PURE__*/ _jsx(SpecraCard, {
                        "data-testid": Testids.preAwardV2.readinessCard,
                        "data-area": area,
                        "data-status": status,
                        className: isSelected ? 'ring-2 ring-primary' : undefined,
                        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                            className: "space-y-3 p-4",
                            children: [
                                /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    className: "flex w-full items-center justify-between gap-2 text-left",
                                    "aria-pressed": isSelected,
                                    onClick: ()=>onSelect(area),
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: area
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "outline",
                                            className: STATUS_TONE[status] ?? STATUS_TONE.NotStarted,
                                            children: status
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: status,
                                    disabled: upsert.isPending || readOnly,
                                    onValueChange: (value)=>upsert.mutate({
                                            preAwardProjectId,
                                            area,
                                            status: value
                                        }),
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            className: "h-8 w-full",
                                            "data-testid": Testids.preAwardV2.readinessStatusSelect,
                                            "data-area": area,
                                            "aria-label": `${area} readiness status`,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: READINESS_STATUSES.map((s)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: s,
                                                    children: s
                                                }, s))
                                        })
                                    ]
                                })
                            ]
                        })
                    }, area);
                })
            })
        ]
    });
}
// --- Bid Baseline: read-only frozen proposal snapshot ---
function BidBaselineScreen({ query, bidBaselineId }) {
    const { data: baseline, isLoading, isError, error } = query;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(SpecraCard, {
            "data-testid": Testids.preAwardV2.baselineSection,
            children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                className: "p-6 text-sm text-muted-foreground",
                children: "Loading frozen bid baseline…"
            })
        });
    }
    if (isError || !baseline) {
        return /*#__PURE__*/ _jsx(SpecraCard, {
            "data-testid": Testids.preAwardV2.baselineSection,
            children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-1 p-6 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-medium",
                        children: "Bid baseline unavailable"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: error instanceof Error ? error.message : `No frozen baseline for ${bidBaselineId}.`
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.preAwardV2.baselineSection,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCard, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Bid Baseline · frozen snapshot"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-2 text-sm",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraBadge, {
                                        variant: "neutral",
                                        children: [
                                            "v",
                                            baseline.version
                                        ]
                                    }),
                                    baseline.frozenAt && /*#__PURE__*/ _jsxs(SpecraBadge, {
                                        variant: "outline",
                                        children: [
                                            "frozen ",
                                            baseline.frozenAt
                                        ]
                                    }),
                                    baseline.frozenBy && /*#__PURE__*/ _jsxs(SpecraBadge, {
                                        variant: "outline",
                                        children: [
                                            "by ",
                                            baseline.frozenBy
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraBadge, {
                                        variant: "outline",
                                        children: [
                                            baseline.sourceArtifactIds.length,
                                            " source artifacts"
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "This snapshot is immutable — it was frozen when the proposal was promoted and is the authoritative baseline the award comparison diffs against."
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(JsonSection, {
                title: "Technical commitments",
                json: baseline.technicalCommitmentsJson
            }),
            /*#__PURE__*/ _jsx(JsonSection, {
                title: "Staffing plan",
                json: baseline.staffingPlanJson
            }),
            /*#__PURE__*/ _jsx(JsonSection, {
                title: "Pricing baseline",
                json: baseline.pricingBaselineJson
            }),
            /*#__PURE__*/ _jsx(JsonSection, {
                title: "Planned deliverables",
                json: baseline.plannedDeliverablesJson
            }),
            /*#__PURE__*/ _jsx(JsonSection, {
                title: "Assumptions",
                json: baseline.assumptionsJson
            })
        ]
    });
}
function JsonSection({ title, json }) {
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    className: "text-sm",
                    children: title
                })
            }),
            /*#__PURE__*/ _jsx(SpecraCardContent, {
                className: "text-sm",
                children: json?.trim() && json.trim() !== '{}' && json.trim() !== '[]' ? /*#__PURE__*/ _jsx("pre", {
                    className: "overflow-auto rounded bg-muted p-2 text-xs",
                    children: prettyJson(json)
                }) : /*#__PURE__*/ _jsx("p", {
                    className: "text-muted-foreground",
                    children: "Not captured in this baseline."
                })
            })
        ]
    });
}
// --- Launch: pre-award state + the award-document intake/compare panel ---
function LaunchScreen({ preAwardProjectId, zouProjectId, state, readyCount, onComparison }) {
    const allReady = readyCount === READINESS_AREAS.length;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs(SpecraCard, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Launch · Award Comparison"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-2 text-sm",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "Pre-award state"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "dark",
                                        "data-testid": Testids.preAwardV2.launchStateBadge,
                                        children: state
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-muted-foreground",
                                children: [
                                    readyCount,
                                    "/",
                                    READINESS_AREAS.length,
                                    " readiness areas are Ready",
                                    allReady ? '.' : ' — you can still launch, but unresolved areas surface as risks.'
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(AwardIntakePanel, {
                preAwardProjectId: preAwardProjectId,
                zouProjectId: zouProjectId,
                onComparison: onComparison
            }, preAwardProjectId)
        ]
    });
}
// --- Right rail: selected readiness area detail ---
function DetailRail({ detail, preAwardProjectId, readinessByArea, upsert }) {
    useEffect(()=>{
        upsert.reset();
    }, [
        detail?.area,
        upsert.reset
    ]);
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        className: "overflow-auto",
        "data-testid": Testids.preAwardV2.detailRail,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    className: "text-sm",
                    children: "Readiness detail"
                })
            }),
            /*#__PURE__*/ _jsx(SpecraCardContent, {
                className: "space-y-2 text-sm",
                children: !detail ? /*#__PURE__*/ _jsx("p", {
                    className: "text-muted-foreground",
                    children: "Select a readiness area to see its detail."
                }) : /*#__PURE__*/ _jsx(ReadinessDetailEditor, {
                    area: detail.area,
                    status: detail.status,
                    dataJson: detail.dataJson,
                    isPending: upsert.isPending,
                    saveError: upsert.isError,
                    onSave: (dataJson)=>upsert.mutate({
                            preAwardProjectId,
                            area: detail.area,
                            // Read live status from the map at call time, not from the
                            // stale detail snapshot — guards against the inline-select
                            // race where mutate #1 resolves before the refetch lands.
                            status: readinessByArea.get(detail.area)?.status ?? detail.status,
                            dataJson
                        })
                }, detail.area)
            })
        ]
    });
}
// prettyJson pretty-prints a JSON blob, falling back to the raw string when it
// doesn't parse — the baseline/readiness JSON is pangaea-stored and trusted,
// but a malformed row shouldn't blank the screen.
function prettyJson(raw) {
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch  {
        return raw;
    }
}
